import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, Input } from 'rsuite';
import { RunningClubMedia, UpsertRunningClubMedia } from '@/models/running-club-media.model';
import { upsertRunningClubMedia } from '@/services/running-club-media.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getRunningClubsLookup } from '@/services/running-club.service';
import ImageUploader from '@/components/ImageUploader';
import { ExtensionTypeEnum, ExtensionTypeList, ObjectStorageFileCategory } from '@/models/storage.model';

const modelSchema = Schema.Model<UpsertRunningClubMedia>({
  id: Schema.Types.NumberType(),
  link: Schema.Types.StringType().isRequired('This field is required.'),
  runningClubId: Schema.Types.NumberType().isRequired('This field is required.'),
  extensionType: Schema.Types.NumberType().isRequired('This field is required.'),
});

interface RunningClubMediaDrawerProps extends DrawerProps{
  runningClubId?: number,
};

const RunningClubMediaDrawer : React.FC<RunningClubMediaDrawerProps> = ({runningClubId, open, onClose, ...rest}) => {
  const emptyModel: UpsertRunningClubMedia = {
    id: undefined,
    link: undefined,
    extensionType: undefined,
    runningClubId: runningClubId,
  };

  const [formValue, setFormValue] = useState<UpsertRunningClubMedia>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    setFormValue(emptyModel);
  }, [runningClubId]);

  const handleDrawerClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue(emptyModel);
    setErrorModel(undefined);
    setLoading(false);
    if(onClose){
      onClose(event);
    }
  };

  const handleSubmit = async event => {
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<RunningClubMedia> = await upsertRunningClubMedia(formValue);
      if (result.success) {
        handleDrawerClose(event);
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }
    finally{
      setLoading(false);
    }
  };

  const getExtension = (extension?: string) => {
    switch (extension?.toLowerCase())
    {
      case "jpg" :
      case "jpeg" :
        return ExtensionTypeEnum.Jpg;
      case "png" :
        return ExtensionTypeEnum.Png;
      case "svg" :
        return ExtensionTypeEnum.Svg;
      default: return undefined;
    }
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" 
      open={open}
      onClose={onClose} 
      {...rest}
    >
      <Drawer.Header>
        <Drawer.Title>Add a new running club media</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">
            Submit
          </Button>
          <Button onClick={handleDrawerClose} appearance="subtle">
            Cancel
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form name='upsert-continent' fluid ref={formRef}
          onCheck={setFormError}
          formValue={formValue}
          model={modelSchema}>
          <Form.Group>
            <Form.Label>Running Club</Form.Label>
            <Form.Control name="runningClubId" block
              accepter={FetchingSelect}
              fetchData={(text, page) => getRunningClubsLookup(text, page)}
              disabled={!!runningClubId}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Media</Form.Label>
              <Form.Control
                accepter={ImageUploader}
                name="link"
                height="300px"
                url={formValue.link}
                accept=".jpg,.jpeg,.png,.svg" 
                objectStorageCategory={ObjectStorageFileCategory.RunningClub}
                onChange={(url, e) => {
                  const {fileExtension } = e || {};
                  setFormValue(pre => ({...pre, link: url, extensionType: getExtension(fileExtension)}));
                }}
              />
          </Form.Group>
          <Form.Group>
            <Form.Label>File Extension</Form.Label>
            <Input value={ExtensionTypeList.findLast(ex => ex.value == formValue.extensionType)?.label ?? 'Unknown'} disabled/>
          </Form.Group>

        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
      </Drawer.Body>
    </Drawer>
  );
};

export default RunningClubMediaDrawer;
